import { Trans, useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router-dom'
import { Close } from '@mui/icons-material'
import { Box, Link } from '@mui/material'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import { alpha } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import PropTypes from 'prop-types'

import UpgradePlanLink from '@/common/components/UpgradePlanLink/UpgradePlanLink'
import { getNestingMethodLabel } from '@/common/helpers/MaterialConsumptionModes'

const classes = {
    root: {
        margin: 0,
        padding: 2,
    },
    img: {
        '-webkit-filter': 'grayscale(1)' /* Google Chrome, Safari 6+ & Opera 15+ */,
        filter: 'grayscale(1)' /* Microsoft Edge and Firefox 35+ */,
    },
    closeButton: {
        position: 'absolute',
        right: 1,
        top: 1,
        color: 'grey[500]',
    },
    dialogBackdrop: {
        backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.9),
    },
    methodInfo: {
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        mb: 1.5,
    },
}

/**
 * Dialog that displays the nest preview image for a quote item, plus contextual
 * information about which nesting algorithm produced it and where to manage settings.
 *
 * The "Nesting method" label is derived per `getNestingMethodLabel`: sheet items use the
 * org-level `nestingAlgorithmToUse` (with `rectangularNestingForSingularParts` + `quantity`
 * resolving the singular-parts carve-out); rotary items use `materialConsumptionMode`.
 * This matches what the "Review your nesting settings" link below sends the user to view.
 *
 * @param {Object} props
 * @param {Function} props.handleCloseNest - Called when the dialog is dismissed
 * @param {string} [props.nestImageUri] - URI of the nest preview image
 * @param {Function} [props.onClose] - Optional close handler for the title-bar X button
 * @param {boolean} props.openNest - Controls dialog visibility
 * @param {boolean} [props.organisationNestingEnabled=false] - When false, shows an upgrade prompt instead of the image
 * @param {string} [props.title] - Quote item name shown in the dialog title
 * @param {string} [props.materialConsumptionMode] - Quote item value used to label rotary items
 * @param {string} [props.nestingAlgorithmToUse] - Org setting used to label sheet items
 * @param {boolean} [props.rectangularNestingForSingularParts] - Org flag: flips quantity-1 sheets back to Rectangular Bounds when org is set to Toolpath
 * @param {number} [props.quantity] - Quote item quantity; combined with the singular-parts flag
 * @param {boolean} [props.isRotary=false] - Whether the quote item uses a rotary material (affects label wording)
 * @returns {JSX.Element}
 */
const QuoteCalculatorNestDialog = ({
    handleCloseNest,
    isRotary = false,
    materialConsumptionMode,
    nestImageUri,
    nestingAlgorithmToUse,
    onClose = null,
    openNest,
    organisationNestingEnabled = false,
    quantity,
    rectangularNestingForSingularParts,
    title,
}) => {
    const { t } = useTranslation()

    return (
        <Dialog
            aria-labelledby="nest-dialog"
            maxWidth="lg"
            open={openNest}
            slotProps={{
                backdrop: {
                    sx: classes.dialogBackdrop,
                },
            }}
            onClose={handleCloseNest}
        >
            <DialogTitle>
                <Typography variant="h6">{t('Nest for part:') + ' ' + title}</Typography>
                {onClose ? (
                    <IconButton
                        aria-label="close"
                        size="large"
                        sx={classes.closeButton}
                        onClick={onClose}
                    >
                        <Close />
                    </IconButton>
                ) : null}
            </DialogTitle>

            <DialogContent dividers>
                {organisationNestingEnabled ? (
                    <>
                        <Box
                            data-testid="nest-method-info"
                            sx={classes.methodInfo}
                        >
                            <Typography variant="body2">
                                {t('Nesting method')}
                                {': '}
                                <Box
                                    component="span"
                                    sx={{ fontWeight: 'bold' }}
                                >
                                    {t(
                                        getNestingMethodLabel({
                                            isRotary,
                                            materialConsumptionMode,
                                            nestingAlgorithmToUse,
                                            quantity,
                                            rectangularNestingForSingularParts,
                                        })
                                    )}
                                </Box>
                            </Typography>
                            <Typography
                                color="text.secondary"
                                variant="body2"
                            >
                                <Trans t={t}>
                                    Review your nesting settings{' '}
                                    <Link
                                        component={RouterLink}
                                        data-testid="nest-settings-link"
                                        to="/settings/organisation#nesting"
                                    >
                                        here
                                    </Link>
                                    .
                                </Trans>
                            </Typography>
                        </Box>
                        <Box
                            alt="Nest View"
                            component="img"
                            src={nestImageUri}
                            sx={classes.img}
                        />
                    </>
                ) : (
                    <UpgradePlanLink />
                )}
            </DialogContent>
        </Dialog>
    )
}

QuoteCalculatorNestDialog.propTypes = {
    handleCloseNest: PropTypes.func,
    isRotary: PropTypes.bool,
    materialConsumptionMode: PropTypes.string,
    nestImageUri: PropTypes.string,
    nestingAlgorithmToUse: PropTypes.string,
    openNest: PropTypes.bool,
    organisationNestingEnabled: PropTypes.bool,
    quantity: PropTypes.number,
    rectangularNestingForSingularParts: PropTypes.bool,
    title: PropTypes.string,
    onClose: PropTypes.func,
}

export default QuoteCalculatorNestDialog
